import { useEffect, useState, useCallback } from "react";

const isStandalone = () =>
  typeof window !== "undefined" && (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );

const isIOS = () =>
  typeof window !== "undefined" &&
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) &&
  !window.MSStream;

/**
 * useInstallPrompt — shared PWA install state.
 *
 * Returns:
 *   canInstall:  true if either the Android/Chrome `beforeinstallprompt` event has fired,
 *                or the device is iOS (where we show our custom instructions modal instead).
 *   installed:   true if the app is already running in standalone display mode.
 *   isIos:       true on iOS Safari (so callers can show the Share→Add-to-Home-Screen hint).
 *   promptInstall(): triggers either the native prompt or returns a special signal "ios"
 *                    so the caller can open its own modal.
 */
const useInstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [iosFlag, setIosFlag] = useState(false);

  useEffect(() => {
    setInstalled(isStandalone());
    setIosFlag(isIOS());
    const handler = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", handler);
    const onInstalled = () => setInstalled(true);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      return choice.outcome === "accepted" ? "installed" : "dismissed";
    }
    if (iosFlag) return "ios";
    return "unavailable";
  }, [deferred, iosFlag]);

  return {
    canInstall: !!deferred || iosFlag,
    installed,
    isIos: iosFlag,
    promptInstall,
  };
};

export default useInstallPrompt;
